/**
 * @file AnimatedElement.tsx
 * @description Reusable animated element component for consistent animations
 *
 * @cursorRules
 * - animation-guidelines.mdc - Animation principles
 * - modern-component-patterns.mdc - Next.js component patterns
 */
"use client"

import React, { ElementType, ReactNode } from "react"
import { motion, MotionProps, Variants } from "framer-motion"

interface AnimatedElementProps extends Omit<MotionProps, "animate"> {
  children: ReactNode
  className?: string
  /** Delay in seconds before animation starts */
  delay?: number
  /** HTML element to render as (div, h1, h2, p, etc.) */
  as?: ElementType
  /** Use 'animate' for immediate animation, 'whileInView' for scroll-triggered */
  animationType?: "animate" | "whileInView"
  /** Optional custom variants when part of a parent animation */
  variants?: Variants
  /** Custom index for staggered animations */
  custom?: number
}

export const AnimatedElement = ({
  children,
  className = "",
  delay = 0,
  as = "div",
  animationType = "whileInView",
  variants,
  custom,
  ...motionProps
}: AnimatedElementProps) => {
  // Default animation values if no variants are provided
  const defaultAnimation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay },
  }

  // Determine props based on whether variants are provided
  const animationProps = variants
    ? {
        variants,
        custom,
        initial: "hidden",
        [animationType === "animate" ? "animate" : "whileInView"]: "visible",
      }
    : {
        initial: defaultAnimation.initial,
        [animationType === "animate" ? "animate" : "whileInView"]:
          defaultAnimation.animate,
        transition: defaultAnimation.transition,
      }

  // Create the motion component with proper typing
  const MotionComponent = motion[as as keyof typeof motion] as typeof motion.div

  return (
    <MotionComponent
      className={className}
      viewport={{ once: true }}
      {...animationProps}
      {...motionProps}
    >
      {children}
    </MotionComponent>
  )
}
